import React from 'react';
import {
    Table,
    Tag,
    Button,
    Space,
    Spin,
    Popconfirm,
    message,
} from 'antd';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetAgencyBillingSummaryQuery,
    useReleaseAgencyBillingMutation,
} from '../../services/nestjs-base-api';
import { FormattedMessage } from 'react-intl';
import { Card } from '../../components/MarketerView/atoms';
import { AgencyPageHeader, StatTile } from '../components/agencyUi';

export const AgencyBillingPage: React.FC = () => {
    const { user } = useAuth();
    const { data: billingSummary, isLoading } = useGetAgencyBillingSummaryQuery({
        jwtToken: user?.jwtToken || '',
    });
    const [releaseBilling, { isLoading: isReleasing }] = useReleaseAgencyBillingMutation();

    const handleReleaseBilling = async (clientOrgId: number) => {
        try {
            await releaseBilling({
                jwtToken: user?.jwtToken || '',
                clientOrgId,
            }).unwrap();
            message.success('Billing released successfully');
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to release billing');
        }
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    const columns = [
        {
            title: <FormattedMessage id="agency.billing.client" defaultMessage="Client" />,
            dataIndex: 'clientOrganisationName',
            key: 'clientOrganisationName',
        },
        {
            title: <FormattedMessage id="agency.billing.plan" defaultMessage="Plan" />,
            dataIndex: 'planName',
            key: 'planName',
            render: (planName: string | null) => planName || '-',
        },
        {
            title: <FormattedMessage id="agency.billing.status" defaultMessage="Status" />,
            dataIndex: 'subscriptionStatus',
            key: 'subscriptionStatus',
            render: (status: string | null) => {
                if (!status) return '-';
                const colorMap: Record<string, string> = {
                    active: 'green',
                    trialing: 'blue',
                    past_due: 'orange',
                    canceled: 'red',
                };
                return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
            },
        },
        {
            title: <FormattedMessage id="agency.billing.paidBy" defaultMessage="Paid By" />,
            dataIndex: 'billingResponsibility',
            key: 'billingResponsibility',
            render: (responsibility: string) => (
                <Tag color={responsibility === 'AGENCY' ? 'gold' : 'default'}>
                    {responsibility === 'AGENCY' ? (
                        <FormattedMessage id="agency.billing.agencyPays" defaultMessage="Agency" />
                    ) : (
                        <FormattedMessage id="agency.billing.clientPays" defaultMessage="Client" />
                    )}
                </Tag>
            ),
        },
        {
            title: <FormattedMessage id="agency.billing.periodEnd" defaultMessage="Period End" />,
            dataIndex: 'currentPeriodEnd',
            key: 'currentPeriodEnd',
            render: (date: string | null) =>
                date ? new Date(date).toLocaleDateString() : '-',
        },
        {
            title: <FormattedMessage id="agency.billing.actions" defaultMessage="Actions" />,
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    {record.billingResponsibility === 'AGENCY' && (
                        <Popconfirm
                            title={
                                <FormattedMessage
                                    id="agency.billing.releaseConfirm"
                                    defaultMessage="Release billing for this client? They will need to add their own payment method."
                                />
                            }
                            onConfirm={() => handleReleaseBilling(record.clientOrganisationId)}
                            okText="Release"
                            cancelText="Cancel"
                        >
                            <Button
                                size="small"
                                danger
                                loading={isReleasing}
                            >
                                <FormattedMessage id="agency.billing.release" defaultMessage="Release" />
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <AgencyPageHeader
                title={<FormattedMessage id="agency.billing.title" defaultMessage="Billing" />}
                subtitle={
                    <FormattedMessage
                        id="agency.billing.subtitle"
                        defaultMessage="Who pays for what across your client portfolio."
                    />
                }
            />

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 240px))',
                    gap: 16,
                    marginBottom: 24,
                }}
            >
                <StatTile
                    label={
                        <FormattedMessage
                            id="agency.billing.agencyPaidCount"
                            defaultMessage="Agency Paid"
                        />
                    }
                    value={billingSummary?.totalAgencyPaidClients || 0}
                />
                <StatTile
                    label={
                        <FormattedMessage
                            id="agency.billing.clientPaidCount"
                            defaultMessage="Client Paid"
                        />
                    }
                    value={billingSummary?.totalClientPaidClients || 0}
                />
            </div>

            <Card pad={0}>
                <Table
                    columns={columns}
                    dataSource={billingSummary?.clients || []}
                    rowKey="clientOrganisationId"
                    pagination={false}
                    size="middle"
                />
            </Card>
        </div>
    );
};
